/**
 * Is this combatant still IN THE FIGHT - on their feet and able to matter?
 *
 * `isDowned` (engine/conditions) answers it for a person: dead, unconscious
 * or dying. It says nothing about a horde, which carries none of those
 * conditions and is simply gone at magnitude 0. This is the question the
 * phases ask - who still stands against the party, who notices, who is
 * ticked when time passes - so it reads the live projection, pins and all.
 *
 * An OBJECT is never in it (2026-10-26): a thing to attack or defend takes no part - no side, no threat, no
 * notice, no tick. It can still be targeted; that is the map's and the rail's business, not this question's.
 *
 * (Not "standing": `roster/standing.ts` is the library's saved condition.)
 */
import { isDowned } from '../conditions';
import { isCrowd, isObject } from '../bodyShape';
import { projectDefender } from './projection';
import type { Combatant } from './types';

export function isInTheFight(c: Combatant): boolean {
  if (isObject(c.sheet)) return false;
  const target = projectDefender(c);
  if (isDowned(target.conditions)) return false;
  return !(isCrowd(target) && (target.horde?.magnitude ?? 0) <= 0);
}

/** The roster without its Objects (2026-10-26): the people, who are cast, hide, watch and can be acted as. */
export function peopleOf<C extends Pick<Combatant, 'sheet'>>(roster: readonly C[]): C[] {
  return roster.filter((c) => !isObject(c.sheet));
}
